/**
 * GhostPreview — translucent voxel preview that follows the pointer during
 * a drag-and-drop from the ShapePanel (T-1.3.1 → T-1.3.2).
 *
 * Responsibilities:
 *   - T-1.3.2:
 *       show(coord)  : place a single semi-transparent box at the snapped
 *                      grid cell so the user sees where the drop will land.
 *       update(coord): move the existing ghost to a new cell without
 *                      re-creating the mesh (one mesh per drag session).
 *       hide()       : tear down the mesh and dispose GPU resources
 *                      (geometry + material) when the drag leaves or
 *                      completes.
 *   - T-1.3.4:
 *       showConflict(coord): switch the ghost to the red CONFLICT_COLOR
 *                      and show it at `coord` (creating the mesh if
 *                      absent, recolouring it if it was already showing
 *                      in blue). Indicates the drop target cell is
 *                      already occupied in the voxels Map. The drop is
 *                      still allowed to land (PlaceVoxel — T-1.3.5 — will
 *                      decide policy; the ghost is a *hint*, not a gate).
 *
 * What this class deliberately does NOT do:
 *   - Place a real voxel in the scene store. That is T-1.3.5's
 *     PlaceVoxel command. The ghost is a *preview*, not a write.
 *   - Render any voxel solid. Until T-1.3.5 the scene contains no
 *     voxels at all — only the grid floor.
 *   - Build its own Scene. Constitution §2.3 #3 forbids a second 3D
 *     pipeline. The scene is injected; this class only adds/removes
 *     one mesh inside it.
 *
 * The voxel-to-world mapping: a VoxelCoord `[x, y, z]` is the *min* corner
 * of the cell (SPEC §2.1). Three.js renders mesh.position as the *center*
 * of the box. So we offset by +0.5 on every axis. The box is 0.98 wide on
 * each side (not 1.0) to avoid z-fighting against the grid floor and
 * against future solid voxels at the same coord.
 *
 * Re-entrancy: show() while already showing is a no-op. This keeps the
 * drag handler simple — it can call show() once on dragenter and then
 * only call update() as the pointer moves, without bookkeeping.
 */

import { BoxGeometry, Mesh, MeshBasicMaterial, type Scene } from 'three';
import type { VoxelCoord } from '@/state/types';

// Box is 0.98 instead of 1.0 to leave a hairline gap and avoid z-fighting
// against the grid floor (and later, against placed voxels at the same
// coord). Visually indistinguishable from a 1.0 cube.
const GHOST_BOX_SIZE = 0.98;

// World-space center of the voxel at integer coord `c` = c + 0.5.
const MIN_TO_CENTER_OFFSET = 0.5;

// Material colour (PRD §3.1 primary blue). Half-bright than the solid
// voxel default (#4A90E2 @ 1.0) so the ghost reads as "pending".
const GHOST_COLOR = 0x4a90e2;
// T-1.3.4 — red used to flag "this cell is already occupied". Saturated
// enough to read against the blue grid floor, but not alarm-red; the
// drop is still legal (PlaceVoxel — T-1.3.5 — decides policy).
const CONFLICT_COLOR = 0xff4444;
const GHOST_OPACITY = 0.4;

export class GhostPreview {
  private mesh: Mesh | null = null;
  private material: MeshBasicMaterial | null = null;
  private readonly scene: Scene;

  constructor(scene: Scene) {
    this.scene = scene;
  }

  /**
   * Make the ghost visible at `coord`. No-op if the ghost is already
   * showing. Idempotent — the drag handler relies on this so it can
   * call show() on dragenter and update() thereafter without keeping
   * its own "is the ghost up?" flag.
   */
  show(coord: VoxelCoord): void {
    if (this.mesh) {
      // If the mesh already exists but is showing the conflict colour
      // (e.g. user dragged from an occupied cell into a fresh cell),
      // reset it back to blue so the ghost honestly reflects state.
      this.applyMaterial(GHOST_COLOR);
      this.positionAt(coord);
      return;
    }
    this.createMesh(coord, GHOST_COLOR);
  }

  /**
   * Move the visible ghost to `coord`. No-op when the ghost is not
   * currently showing — callers should not need to know whether show()
   * has been called yet (the drag handler fires update() on every
   * dragover regardless of ghost state).
   */
  update(coord: VoxelCoord): void {
    if (!this.mesh) {
      return;
    }
    this.positionAt(coord);
  }

  /**
   * T-1.3.4 — switch the ghost to the red CONFLICT_COLOR at `coord`.
   * Creates the mesh on first call (so a dragenter that lands on an
   * already-occupied cell still produces a visible hint), or recolours
   * an already-showing mesh back to red if the user re-enters an
   * occupied cell mid-drag. Idempotent — safe to call repeatedly.
   */
  showConflict(coord: VoxelCoord): void {
    if (this.mesh) {
      this.applyMaterial(CONFLICT_COLOR);
      this.positionAt(coord);
      return;
    }
    this.createMesh(coord, CONFLICT_COLOR);
  }

  /**
   * Remove the ghost from the scene and dispose GPU resources. Safe to
   * call when no ghost is showing (no-op).
   */
  hide(): void {
    if (!this.mesh) {
      return;
    }
    this.scene.remove(this.mesh);
    this.mesh.geometry.dispose();
    // MeshBasicMaterial.dispose() lives on Material; the cast keeps
    // TypeScript honest without pulling in the full Material type here.
    (this.mesh.material as MeshBasicMaterial).dispose();
    this.mesh = null;
    this.material = null;
  }

  private createMesh(coord: VoxelCoord, color: number): void {
    const geometry = new BoxGeometry(GHOST_BOX_SIZE, GHOST_BOX_SIZE, GHOST_BOX_SIZE);
    const material = new MeshBasicMaterial({
      color,
      transparent: true,
      opacity: GHOST_OPACITY,
    });
    this.material = material;
    this.mesh = new Mesh(geometry, material);
    this.positionAt(coord);
    this.scene.add(this.mesh);
  }

  private applyMaterial(color: number): void {
    // Three.js MeshBasicMaterial exposes `color` as a Color instance;
    // `set()` accepts a hex literal and updates the GPU uniform.
    if (!this.material) {
      return;
    }
    this.material.color.set(color);
  }

  private positionAt(coord: VoxelCoord): void {
    // coord is the *min* corner of the voxel cell. Mesh.position is the
    // geometric centre, so we shift by +0.5 on every axis. y is included
    // for symmetry; today's drag handler pins y=0 (T-1.3.3 will let it
    // move freely).
    this.mesh?.position.set(
      coord[0] + MIN_TO_CENTER_OFFSET,
      coord[1] + MIN_TO_CENTER_OFFSET,
      coord[2] + MIN_TO_CENTER_OFFSET,
    );
  }
}
